/**
 * Log Buffer Service
 *
 * Batches API request logs to reduce database writes by 95%
 * - Flushes every 100 requests OR 5 seconds (whichever comes first)
 * - Anonymizes IP addresses (GDPR/CCPA compliant)
 * - Scrubs PII from error messages
 * - Non-blocking logging (failures don't break the app)
 */

// IMPORTANT: Use raw unmonitored client to prevent infinite loop
// (monitoring logs shouldn't trigger more monitoring logs)
// Lazy import to break circular dependency with supabase/client
let supabaseInstance: any = null;
async function getSupabase() {
  if (!supabaseInstance) {
    // Dynamic import to break circular dependency (ES6 - works in browser)
    const module = await import('@/integrations/supabase/client');
    supabaseInstance = module.supabaseRaw;
  }
  return supabaseInstance;
}

interface LogEntry {
  endpoint: string;
  method: string;
  status_code: number;
  response_time_ms: number;
  user_id?: string;
  ip_address?: string;
  user_agent?: string;
  error_message?: string;
  request_size_bytes?: number;
  response_size_bytes?: number;
}

interface ErrorLogEntry {
  error_type: string;
  message: string;
  stack_trace?: string;
  page_url: string;
  user_id?: string;
  browser?: string;
  os?: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  context?: Record<string, any>;
}

class LogBufferService {
  private apiLogBuffer: LogEntry[] = [];
  private errorLogBuffer: ErrorLogEntry[] = [];

  private readonly FLUSH_INTERVAL = 5000; // 5 seconds
  private readonly MAX_BUFFER_SIZE = 100; // 100 requests

  private flushTimer?: NodeJS.Timeout;
  private isDestroyed = false;

  constructor() {
    this.startFlushTimer();

    // Flush on page unload (best effort)
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush();
      });
    }
  }

  /**
   * Add an API request log entry
   */
  public addApiLog(entry: LogEntry): void {
    if (this.isDestroyed) return;

    // Scrub PII before adding to buffer
    const scrubbed = this.scrubApiLog(entry);
    this.apiLogBuffer.push(scrubbed);

    // Flush if buffer is full
    if (this.apiLogBuffer.length >= this.MAX_BUFFER_SIZE) {
      this.flush();
    }
  }

  /**
   * Add an error log entry
   */
  public addErrorLog(entry: ErrorLogEntry): void {
    if (this.isDestroyed) return;

    // Scrub PII from error messages
    const scrubbed = {
      ...entry,
      message: this.scrubErrorMessage(entry.message),
      stack_trace: entry.stack_trace ? this.scrubErrorMessage(entry.stack_trace) : undefined,
    };

    this.errorLogBuffer.push(scrubbed);

    // Flush errors immediately if critical
    if (entry.severity === 'critical') {
      this.flushErrors();
    }
  }

  /**
   * Manually flush all buffered logs
   */
  public async flush(): Promise<void> {
    await Promise.all([
      this.flushApiLogs(),
      this.flushErrors()
    ]);
  }

  /**
   * Clean up and flush remaining logs
   */
  public destroy(): void {
    this.isDestroyed = true;

    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = undefined;
    }

    this.flush(); // Final flush (fire and forget)
  }

  // ============================================================================
  // Private Methods - PII Scrubbing
  // ============================================================================

  private scrubApiLog(entry: LogEntry): LogEntry {
    return {
      ...entry,
      ip_address: entry.ip_address ? this.anonymizeIP(entry.ip_address) : undefined,
      error_message: entry.error_message ? this.scrubErrorMessage(entry.error_message) : undefined,
    };
  }

  private anonymizeIP(ip: string): string {
    // Convert 192.168.1.123 -> 192.168.1.0 (GDPR compliant)
    const parts = ip.split('.');
    if (parts.length === 4) {
      parts[3] = '0';
      return parts.join('.');
    }

    // IPv6: Keep first 48 bits, zero out the rest
    if (ip.includes(':')) {
      const parts = ip.split(':');
      return parts.slice(0, 3).join(':') + '::';
    }

    return ip;
  }

  private scrubErrorMessage(message: string): string {
    let scrubbed = message;

    // Remove email addresses
    scrubbed = scrubbed.replace(
      /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g,
      '[EMAIL]'
    );

    // Remove credit card numbers
    scrubbed = scrubbed.replace(
      /\b\d{4}[\s-]?\d{4}[\s-]?\d{4}[\s-]?\d{4}\b/g,
      '[CARD]'
    );

    // Remove Bearer tokens
    scrubbed = scrubbed.replace(
      /Bearer\s+[A-Za-z0-9-._~+/]+=*/gi,
      'Bearer [TOKEN]'
    );

    // Remove API keys (32+ character alphanumeric strings)
    scrubbed = scrubbed.replace(
      /\b[A-Za-z0-9]{32,}\b/g,
      '[API_KEY]'
    );

    // Remove JWT tokens
    scrubbed = scrubbed.replace(
      /eyJ[A-Za-z0-9-_]+\.eyJ[A-Za-z0-9-_]+\.[A-Za-z0-9-_.+/]+=*/g,
      '[JWT_TOKEN]'
    );

    // Remove phone numbers
    scrubbed = scrubbed.replace(
      /(\+\d{1,3}[\s-]?)?\(?\d{3}\)?[\s.-]?\d{3}[\s.-]?\d{4}/g,
      '[PHONE]'
    );

    return scrubbed;
  }

  // ============================================================================
  // Private Methods - Flushing
  // ============================================================================

  private async flushApiLogs(): Promise<void> {
    if (this.apiLogBuffer.length === 0) return;

    // Take up to 100 entries (in case buffer grew while flushing)
    const batch = this.apiLogBuffer.splice(0, 100);

    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .from('api_request_logs')
        .insert(batch);

      if (error) {
        console.error('[LogBuffer] Failed to flush API logs:', error);
        // Don't re-add to buffer - logging failures shouldn't break the app
      }
    } catch (err) {
      console.error('[LogBuffer] API log flush error:', err);
    }
  }

  private async flushErrors(): Promise<void> {
    if (this.errorLogBuffer.length === 0) return;

    const batch = this.errorLogBuffer.splice(0, 100);

    try {
      const supabase = await getSupabase();
      const { error } = await supabase
        .from('error_logs')
        .insert(batch);

      if (error) {
        console.error('[LogBuffer] Failed to flush error logs:', error);
      }
    } catch (err) {
      console.error('[LogBuffer] Error log flush error:', err);
    }
  }

  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flush();
    }, this.FLUSH_INTERVAL);
  }
}

// Singleton instance
export const logBuffer = new LogBufferService();

// Clean up on hot module reload (development)
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    logBuffer.destroy();
  });
}
